import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {unionBy} from "lodash-es";
import {format} from "date-fns";
import {Client} from "@/api/dto/dto";
import {useRosterStore} from "@/store/roster_store";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import ClientsTable, {ClientsSearchBy} from "@clients/ClientsTable";
import {dateRange, FilterInvalidate} from "@common/FilterComponent";
import {useFilterQuery} from "@/hooks/useFilterQuery";
import {useSearchQuery} from "@/hooks/useSearchQuery";
import {useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import Wrapper from '@common/pages/Wrapper';
import {MixpanelClientListEvent, MixpanelEventType} from "@/api/dto/mixpanel";
import {SORT_ORDER, TableColumn} from "@common/SortableTable";
import {useMixpanel} from "@/hooks/useMixpanel";
import {displayValueToGroupTypes} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants";

export type SortOrderType = typeof SORT_ORDER.ASC | typeof SORT_ORDER.DESC | typeof SORT_ORDER.NONE;

const ClientsPage = () => {
  const [clients, setClients] = useState<Client[]>();
  const [searchTotal, setSearchTotal] = useState(0);
  const [rosterClientsTotal, setRosterClientsTotal] = useState(0);
  const sortKeyRef = useRef<string | undefined>('lastEvent');
  const sortOrderRef = useRef<SortOrderType | undefined>(SORT_ORDER.DESC);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [currentSearchBy, setCurrentSearchBy] = useState(() => {
    return localStorage.getItem('search_by_clients_page') || ClientsSearchBy.NAME;
  });
  const [searchTermLength, setSearchTermLength] = useState(0);
  const queryClient = useQueryClient();
  const currentRoster = useRosterStore((state) => state.currentRoster);
  const currentRosterId = currentRoster?.id ?? 0;
  const {carecoApi} = useContext(CarecoApiContext);

  const invalidate = useCallback<FilterInvalidate>((rosterId = currentRosterId) => {
    queryClient.removeQueries({queryKey: ['clients', rosterId]});
    setClients([]);
  }, [currentRosterId]);
  const {params, queryFnCallback} = useFilterQuery({
    invalidate,
    params: {
      assignedTo: [],
      events: [],
      eventDate: {} as dateRange,
      admissionStatus: []
    },
  });
  const {
    data: clientsData,
    isLoading,
    isFetching,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['clients', currentRosterId],
    queryFn: ({pageParam}) => {
      const events: Set<string> = new Set();
      params.events.forEach((event: string) => {
        const [eventType, eventStatus] = event.split(':');
        // for adt events, we need to include all group types that map to the selected display value
        if (Object.keys(displayValueToGroupTypes).includes(eventType)) {
          for(const groupType of displayValueToGroupTypes[eventType as keyof typeof displayValueToGroupTypes]) {
            events.add(`${groupType}:${eventStatus}`);
          }
        }
        events.add(event.replace(/-/g, '_'));
      });

      return carecoApi?.getClients(
        currentRosterId,
        pageParam,
        currentSearchBy === ClientsSearchBy.NAME ? queryFnSearch() : null,
        currentSearchBy === ClientsSearchBy.CLIENT_ID &&
        !(searchTermLength === 4 && searchTerm.length < searchTermLength) ? queryFnSearch() : null,
        params.assignedTo,
        Array.from(events),
        params.eventDate?.startDate ? format(new Date(params.eventDate.startDate), 'yyyy-MM-dd') : null,
        params.eventDate?.endDate ? format(new Date(params.eventDate.endDate), 'yyyy-MM-dd') : null,
        sortOrderRef?.current?.value,
        sortKeyRef.current,
        params.admissionStatus?.[0] ?? null
      );
    },
    enabled: !(!carecoApi || !currentRoster),
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const {searchTerm, searchFunction, queryFnSearch} = useSearchQuery({
    invalidate,
    queryFnCallback,
    canInvalidate
  });

  function canInvalidate() {
    setSearchTermLength(searchTerm.length);
    return currentSearchBy == ClientsSearchBy.NAME || (currentSearchBy == ClientsSearchBy.CLIENT_ID &&
            (searchTerm.length > 3 || searchTerm.length ===0)) ||
        (currentSearchBy == ClientsSearchBy.CLIENT_ID &&
            searchTermLength ===4 && searchTerm.length < searchTermLength);
  }

  useEffect(() => {
    if (clientsData) {
      const fetchedClients = clientsData.pages.flatMap(page => page?.data.clients ?? []);
      setClients((prevClients) => prevClients ? unionBy(fetchedClients, prevClients, 'patientId') : fetchedClients);

      const clientData = clientsData.pages[0]?.data;
      setSearchTotal(clientData?.searchTotal || 0);
      setRosterClientsTotal(clientData?.rosterClientsTotal || 0);
    }
  }, [clientsData]);

  const handleNextPage = () => {
    if (clientsData?.pages.at(-1)?.data.clients.length) {
      void fetchNextPage();
    }
  };

  const handleSortChange = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    sortKeyRef.current = sortKey.key;
    sortOrderRef.current = sortOrder;
    invalidate();
  };

  const resetSort = (reasonToInvalidate: boolean) =>  {
    sortKeyRef.current = undefined;
    sortOrderRef.current = undefined;
    if (reasonToInvalidate) {
      invalidate();
    }
  };

  const handleSearchByChange = (value: string) => {
    setCurrentSearchBy(value);
    localStorage.setItem('search_by_clients_page', value);
  };

  useEffect(() => {
    // Reset the query cache, otherwise tanstack will refetch X pages instead of only the first page
    invalidate();
    setRosterClientsTotal(0);
  }, [currentRosterId]);

  useEffect(() => {
    if (carecoApi && currentRoster && mixpanelEventProperties) {
      const mixpanelEvent: MixpanelClientListEvent = {
        eventName: MixpanelEventType.BIH_CLIENT_LIST_VIEWED,
        ...mixpanelEventProperties,
        ...getCurrentGroupDetails(currentRoster),
      };

      void carecoApi.postMixpanelEvent(mixpanelEvent);
    }
  }, [carecoApi, currentRoster, mixpanelEventProperties]);

  return (
    <Wrapper>
      {!currentRoster && <div>No group selected</div>}
      {currentRoster &&
        <ClientsTable
          clients={clients || []}
          searchTotal={searchTotal}
          loading={isLoading}
          fetching={isFetching}
          loadError={error}
          searchTerm={searchTerm}
          searchFunction={searchFunction}
          query={{params, invalidate}}
          rosterClientsTotal={rosterClientsTotal}
          sortFunction={handleSortChange}
          resetSortFunction={resetSort}
          handleSearchByChange={handleSearchByChange}
          currentSearchBy={currentSearchBy}
          onNextPage={handleNextPage}
        />
      }
    </Wrapper>
  );
};

export default ClientsPage;
